import React, { useState, useEffect, useRef } from "react";
import { Form, Row, Col } from "react-bootstrap";

import CopyIcon from "./CopyIcon";

interface PhoneInputProps {
  value?: string;
  onChange?: (e: { target: { name?: string; value: string } }) => void;
  name?: string;
  isInvalid?: boolean;
  feedback?: string;
  className?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({ value = "", onChange, name, isInvalid = false, feedback = "", className = "" }) => {
    const [parts, setParts] = useState<string[]>(["", "", ""]);
    const inputRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];

    useEffect(() => {
        if (value) {
            const splitValue = value.split("-");
            if (splitValue.length === 3) {
                setParts(splitValue);
            } else {
                // Fallback for non-formatted values
                const digits = value.replace(/\D/g, "");
                setParts([
                    digits.slice(0, 3),
                    digits.slice(3, 6),
                    digits.slice(6, 10)
                ]);
            }
        } else {
            setParts(["", "", ""]);
        }
    }, [value]);

    const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/\D/g, "");
        if (val.length > (index === 2 ? 4 : 3)) return; // Prevent extra digits

        const newParts = [...parts];
        newParts[index] = val;
        setParts(newParts);

        // Call onChange with formatted value
        if (onChange) {
            onChange({
                target: {
                    name: name,
                    value: newParts.join("-")
                }
            });
        }

        // Auto-focus logic
        const maxLength = index === 2 ? 4 : 3;
        if (val.length >= maxLength && index < 2) {
            inputRefs[index + 1].current?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && !parts[index] && index > 0) {
            inputRefs[index - 1].current?.focus();
        }
    };

    return (
        <div className={`phone-input-container ${className}`}>
            <Row className="gx-1 align-items-center mx-0">
                <Col xs={3} className="px-0">
                    <Form.Control
                        ref={inputRefs[0]}
                        size="sm"
                        type="text"
                        maxLength={3}
                        value={parts[0]}
                        onChange={(e) => handleChange(0, e as any)}
                        onKeyDown={(e) => handleKeyDown(0, e as any)}
                        placeholder="xxx"
                        isInvalid={isInvalid}
                        className="text-center"
                    />
                </Col>
                <Col xs="auto" className="px-1">-</Col>
                <Col xs={3} className="px-0">
                    <Form.Control
                        ref={inputRefs[1]}
                        size="sm"
                        type="text"
                        maxLength={3}
                        value={parts[1]}
                        onChange={(e) => handleChange(1, e as any)}
                        onKeyDown={(e) => handleKeyDown(1, e as any)}
                        placeholder="xxx"
                        isInvalid={isInvalid}
                        className="text-center"
                    />
                </Col>
                <Col xs="auto" className="px-1">-</Col>
                <Col xs={3} className="px-0">
                    <Form.Control
                        ref={inputRefs[2]}
                        size="sm"
                        type="text"
                        maxLength={4}
                        value={parts[2]}
                        onChange={(e) => handleChange(2, e as any)}
                        onKeyDown={(e) => handleKeyDown(2, e as any)}
                        placeholder="xxxx"
                        isInvalid={isInvalid}
                        className="text-center"
                    />
                </Col>
                {value && value.length > 5 && (
                    <Col xs="auto" className="ps-2">
                        <CopyIcon text={value} />
                    </Col>
                )}
            </Row>
            {isInvalid && feedback && (
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {feedback}
                </Form.Control.Feedback>
            )}
        </div>
    );
};

export default PhoneInput;
